import React, { useState } from "react";
import { useSelector } from "react-redux";

import { useRequestVerificationMutation } from "../../../../store/Company/companyApiSlice";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { CommonToast } from "../../../common/Toast";
import { EditDialog } from "./EditDialog";
import { FileDropzone } from "./FileDropzone";
import { SectionNotice } from "./SectionNotice";

/* "Upload supporting documents" — raise a verification request for a section.
 *
 * The endpoint behind it is REAL: `requestVerification` (TIP-491,
 * POST api/company/verify-request/), which the backend gates to active
 * members and answers with a 403 upsell otherwise. That is exactly what the
 * design's "free with your membership" copy describes, and the same mutation
 * the membership card and the public profile already call.
 *
 * What the endpoint does NOT take is documents — its body is `{ companyID }`
 * and nothing else. So the files chosen here are uploaded through the same
 * `uploadFileToS3` every other document on this flow uses, to the section's
 * own path, and then the request is raised. Nothing is silently dropped, but
 * the LINK between a request and its documents is not recorded server-side —
 * a reviewer finds the files under the company, not attached to the request.
 * That needs a backend change. See FINDINGS F-055.
 */

/* What to say when the request is refused. */
export const verificationError = (err) => {
  const said = err?.data?.detail || err?.data?.message;
  if (err?.data?.code === "not_a_member" || err?.status === 403) {
    return said || "Verification is a member-only feature. Become a member to request it.";
  }
  if (err?.status === 401) return "Please sign in again to request verification.";
  return said || "Couldn't send this for verification. Please try again.";
};

export const VerificationModal = ({ spec, onClose, onDone }) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [requestVerification] = useRequestVerificationMutation();

  const submit = async () => {
    if (files.length === 0) {
      setError("Add at least one document for the team to review.");
      return;
    }

    setBusy(true);
    try {
      /* Uploaded first: a verification request with no documents behind it is
         worse than not raising one. */
      for (const file of files) {
        const uploaded = await uploadFileToS3(
          spec.uploadPath,
          file,
          token,
          companyID,
          spec.category
        );
        if (uploaded?.success === false) {
          /* uploadFileToS3 reports its own reason via a toast and resolves
             with success:false rather than throwing. */
          setBusy(false);
          return;
        }
      }

      await requestVerification({ companyID, token }).unwrap();

      CommonToast.success(
        "Sent for verification. The team reviews within 2–3 working days."
      );
      onDone?.();
    } catch (err) {
      /* The endpoint answers with `detail` (not `message`) and, for the member
         gate, a `code`. Both are read here — see verificationError. */
      CommonToast.error(verificationError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EditDialog
      title="Upload supporting documents"
      onClose={onClose}
      ctaLabel="Submit for verification"
      onSubmit={submit}
      submitting={busy}
    >
      <SectionNotice tone="offer" title="Verification is free with your membership">
        Add the documents that back up this section. Our team reviews within 2–3
        working days and marks the data Verified on your profile.
      </SectionNotice>

      <div className="mt-4">
        <p className="m-0 mb-2 text-[13px] font-bold text-[var(--ink)] font-sans">
          Supporting documents
        </p>
        <FileDropzone
          label="Supporting documents"
          multiple
          files={files}
          onChange={(next) => {
            setFiles(next);
            setError("");
          }}
          error={error}
        />
      </div>
    </EditDialog>
  );
};

export default VerificationModal;
